import { useCallback, useRef } from 'react';
import { useLinkModal } from './useLinkModal';
import { useUserFavoritesContext } from '../contexts/UserFavoritesContext';
import { detectKindFromUrl } from '../utils/urlUtils';

/**
 * Drop-in replacement for useLinkModal that automatically
 * tracks how long a resource modal is open and records
 * the viewing session via recordResourceVisit.
 */
export const useTrackedLinkModal = () => {
  const linkModal = useLinkModal();
  const { recordResourceVisit } = useUserFavoritesContext();

  const openedAtRef = useRef<string | null>(null);
  const currentUrlRef = useRef<string>('');
  const currentTitleRef = useRef<string>('');

  const openLink = useCallback((
    url: string,
    title?: string,
    showOpenInNewTab?: boolean,
    allowCredentials?: boolean,
    trustDomain?: boolean
  ) => {
    openedAtRef.current = new Date().toISOString();
    currentUrlRef.current = url;
    currentTitleRef.current = title || '';
    linkModal.openLink(url, title, showOpenInNewTab, allowCredentials, trustDomain);
  }, [linkModal]);

  const openAuthenticatedLink = useCallback((url: string, title?: string) => {
    openedAtRef.current = new Date().toISOString();
    currentUrlRef.current = url;
    currentTitleRef.current = title || '';
    linkModal.openAuthenticatedLink(url, title);
  }, [linkModal]);

  const closeModal = useCallback(() => {
    if (openedAtRef.current && currentUrlRef.current) {
      const closedAt = new Date().toISOString();
      const durationMs = new Date(closedAt).getTime() - new Date(openedAtRef.current).getTime();

      const url = currentUrlRef.current;
      const title = currentTitleRef.current;
      const kind = detectKindFromUrl(url);

      // Fire-and-forget — don't block the close animation
      recordResourceVisit({
        url,
        title,
        kind,
        durationMs,
        openedAt: openedAtRef.current,
        closedAt,
      }).catch(() => {});
    }

    openedAtRef.current = null;
    currentUrlRef.current = '';
    currentTitleRef.current = '';
    linkModal.closeModal();
  }, [linkModal, recordResourceVisit]);

  return {
    ...linkModal,
    openLink,
    openAuthenticatedLink,
    closeModal,
  };
};
